import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import Container from "../components/layout/Container";
import Slider from "../components/ui/Slider";
import ProductCard from "../components/product/ProductCard";
import { fetchSliders, fetchProducts, fetchFeaturedNews } from "../services/api";
import { useLanguage } from "../contexts/LanguageContext";
import { useTranslation } from "../hooks/useTranslation";

// Simple Loading Skeleton
const Skeleton = () => (
  <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg animate-pulse border border-slate-200/50 h-full">
    <div className="bg-slate-200 h-40 sm:h-48 lg:h-56 rounded-t-2xl"></div>
    <div className="p-4 sm:p-6 space-y-3">
      <div className="bg-slate-200 h-3 sm:h-4 rounded w-3/4"></div>
      <div className="bg-slate-200 h-3 sm:h-4 rounded w-1/2"></div>
      <div className="bg-slate-200 h-5 sm:h-6 rounded w-1/3"></div>
    </div>
  </div>
);

// News Card Component
const NewsCard = ({ item, formatDate, t }) => (
  <div className="group bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 overflow-hidden border border-slate-200/50 h-full">
    {item.image && (
      <div className="relative overflow-hidden h-40 sm:h-48">
        <Link to={`/news/${item.id}`}>
          <img 
            src={item.image}
            alt={item.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        </Link>
        {item.is_featured && (
          <div className="absolute top-3 sm:top-4 left-3 sm:left-4">
            <span className="bg-red-500 text-white px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-semibold shadow-lg">
              {t('pages.home.featured') || 'Asosiy'}
            </span>
          </div>
        )}
      </div>
    )}
    <div className="p-4 sm:p-6">
      <h3 className="text-base sm:text-lg font-bold text-slate-900 mb-2 sm:mb-3 line-clamp-2 group-hover:text-red-600 transition-colors duration-300">
        <Link to={`/news/${item.id}`}>{item.title}</Link>
      </h3>
      <p className="text-slate-600 mb-3 sm:mb-4 line-clamp-2 sm:line-clamp-3 text-sm leading-relaxed">
        {item.content}
      </p>
      <div className="flex items-center justify-between pt-2 border-t border-slate-100">
        <span className="text-xs sm:text-sm text-slate-500">
          {formatDate(item.created_at)}
        </span>
        <Link 
          to={`/news/${item.id}`}
          className="text-red-600 hover:text-red-700 font-medium text-xs sm:text-sm transition-colors whitespace-nowrap"
        >
          {t('pages.home.readMore') || 'Batafsil'} →
        </Link>
      </div>
    </div>
  </div>
);

// Slider Controls Component
const SliderControls = ({ onPrev, onNext, title }) => (
  <div className="flex justify-between items-center mb-4 sm:mb-6 lg:hidden px-4">
    <h3 className="text-lg sm:text-xl font-bold text-slate-900">{title}</h3>
    <div className="flex space-x-2">
      <button 
        onClick={onPrev}
        className="p-1.5 sm:p-2 bg-white shadow-md rounded-lg hover:shadow-lg transition-all duration-200 border border-slate-200"
      >
        <svg className="w-4 h-4 sm:w-5 sm:h-5 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
      </button>
      <button 
        onClick={onNext}
        className="p-1.5 sm:p-2 bg-white shadow-md rounded-lg hover:shadow-lg transition-all duration-200 border border-slate-200"
      >
        <svg className="w-4 h-4 sm:w-5 sm:h-5 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </button>
    </div>
  </div>
);

// Empty State Component
const EmptyState = ({ title, icon, t }) => (
  <div className="text-center py-16">
    <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-12 max-w-md mx-auto border border-slate-200/50 shadow-lg">
      <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
        {icon}
      </div>
      <p className="text-slate-600 font-medium">{title}</p>
    </div>
  </div>
);

const Home = () => {
  const [sliders, setSliders] = useState([]);
  const [products, setProducts] = useState([]);
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const { currentLanguage } = useLanguage();
  const { t } = useTranslation();
  
  // Refs for mobile sliders
  const productsSliderRef = useRef(null);
  const newsSliderRef = useRef(null);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        const [slidersData, productsData, newsData] = await Promise.all([
          fetchSliders(currentLanguage).catch(() => []),
          fetchProducts(currentLanguage).catch(() => []),
          fetchFeaturedNews(currentLanguage).catch(() => [])
        ]);
        setSliders(slidersData);
        setProducts(productsData.slice(0, 6));
        setNews(newsData.slice(0, 6));
      } catch (error) {
        console.error('Veri yuklanamadi:', error);
        setSliders([]);
        setProducts([]);
        setNews([]);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [currentLanguage]);

  // Slider scroll functions
  const scrollSlider = (ref, direction) => {
    if (ref.current) {
      const scrollAmount = 300;
      ref.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-red-50/30">
      {/* Hero Slider */}
      <section className="relative">
        <Slider slides={sliders} />
      </section>

      <Container className="py-8">
        {/* Featured Products Section */}
        <section className="py-12 sm:py-16">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl lg:text-5xl font-bold mb-3 sm:mb-4 text-slate-900 px-4">
              {t('pages.home.featuredProducts') || 'Mashhur Mahsulotlar'}
            </h2>
            <p className="text-base sm:text-lg text-slate-600 max-w-2xl mx-auto px-4">
              {t('pages.home.featuredProductsSubtitle') || 'Eng sifatli va mashhur mahsulotlarimiz bilan tanishing'}
            </p>
          </div>

          {/* Mobile Slider Controls */}
          <SliderControls 
            onPrev={() => scrollSlider(productsSliderRef, 'left')}
            onNext={() => scrollSlider(productsSliderRef, 'right')}
            title={t('pages.home.productsTitle') || 'Mahsulotlar'}
          />

          {loading ? (
            <>
              {/* Mobile Loading */}
              <div className="lg:hidden overflow-x-auto pb-4 scrollbar-hide px-4">
                <div className="flex space-x-3 sm:space-x-4" style={{ width: 'max-content' }}>
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="w-72 sm:w-80 flex-shrink-0">
                      <Skeleton />
                    </div>
                  ))}
                </div>
              </div>
              {/* Desktop Loading */}
              <div className="hidden lg:grid lg:grid-cols-3 gap-8">
                {[1, 2, 3].map((i) => <Skeleton key={i} />)}
              </div>
            </>
          ) : products.length > 0 ? (
            <>
              {/* Mobile Slider */}
              <div className="lg:hidden">
                <div 
                  ref={productsSliderRef}
                  className="overflow-x-auto pb-4 scrollbar-hide px-4"
                >
                  <div className="flex space-x-4 sm:space-x-6" style={{ width: 'max-content' }}>
                    {products.map((product) => (
                      <div key={product.id} className="min-w-[260px] sm:min-w-[280px]">
                        <ProductCard product={product} showButton={true} />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              
              {/* Desktop Grid */}
              <div className="hidden lg:grid lg:grid-cols-3 gap-8">
                {products.slice(0, 3).map((product, index) => (
                  <div 
                    key={product.id} 
                    className="transform hover:scale-105 transition-all duration-300"
                    style={{ 
                      animationDelay: `${index * 0.1}s`,
                      animation: 'fadeInUp 0.6s ease-out forwards'
                    }}
                  >
                    <ProductCard product={product} showButton={true} />
                  </div>
                ))}
              </div>
            </>
          ) : (
            <EmptyState 
              title={t('pages.home.noProducts') || 'Mahsulot topilmadi'}
              icon={
                <svg className="w-8 h-8 text-slate-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 2L3 7v11a2 2 0 002 2h10a2 2 0 002-2V7l-7-5zM10 12a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
                </svg>
              }
              t={t}
            />
          )}

          <div className="text-center mt-8 sm:mt-12 px-4">
            <Link 
              to="/products" 
              className="inline-flex items-center bg-red-600 hover:bg-red-700 text-white font-semibold py-3 sm:py-4 px-6 sm:px-8 rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl text-sm sm:text-base"
            >
              <span className="mr-2">{t('pages.home.allProducts') || 'Barcha Mahsulotlar'}</span>
              <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
          </div>
        </section>

        {/* Latest News Section */}
        <section className="py-12 sm:py-16">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl lg:text-5xl font-bold mb-3 sm:mb-4 text-slate-900 px-4">
              {t('pages.home.latestNews') || "So'nggi Yangiliklar"}
            </h2>
            <p className="text-base sm:text-lg text-slate-600 max-w-2xl mx-auto px-4">
              {t('pages.home.latestNewsSubtitle') || "Kompaniyamiz haqida eng so'nggi yangiliklar va ma'lumotlar"}
            </p>
          </div>

          {/* Mobile Slider Controls */}
          <SliderControls 
            onPrev={() => scrollSlider(newsSliderRef, 'left')}
            onNext={() => scrollSlider(newsSliderRef, 'right')}
            title={t('pages.home.newsTitle') || 'Yangiliklar'}
          />

          {loading ? (
            <>
              {/* Mobile Loading */}
              <div className="lg:hidden overflow-x-auto pb-4 scrollbar-hide px-4">
                <div className="flex space-x-3 sm:space-x-4" style={{ width: 'max-content' }}>
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="w-72 sm:w-80 flex-shrink-0">
                      <Skeleton />
                    </div>
                  ))}
                </div>
              </div>
              {/* Desktop Loading */}
              <div className="hidden lg:grid lg:grid-cols-3 gap-8">
                {[1, 2, 3].map((i) => <Skeleton key={i} />)}
              </div>
            </>
          ) : news.length > 0 ? (
            <>
              {/* Mobile Slider */}
              <div className="lg:hidden">
                <div 
                  ref={newsSliderRef}
                  className="overflow-x-auto pb-4 scrollbar-hide px-4"
                >
                  <div className="flex space-x-3 sm:space-x-4" style={{ width: 'max-content' }}>
                    {news.map((item) => (
                      <div key={item.id} className="w-72 sm:w-80 flex-shrink-0">
                        <NewsCard item={item} formatDate={formatDate} t={t} />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              
              {/* Desktop Grid */}
              <div className="hidden lg:grid lg:grid-cols-3 gap-8">
                {news.slice(0, 3).map((item, index) => (
                  <div 
                    key={item.id}
                    style={{ 
                      animationDelay: `${index * 0.1}s`,
                      animation: 'fadeInUp 0.6s ease-out forwards'
                    }}
                  >
                    <NewsCard item={item} formatDate={formatDate} t={t} />
                  </div>
                ))}
              </div>
            </>
          ) : (
            <EmptyState 
              title={t('pages.home.noNews') || 'Yangilik topilmadi'}
              icon={
                <svg className="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
                </svg>
              }
              t={t}
            />
          )}

          <div className="text-center mt-8 sm:mt-12 px-4">
            <Link 
              to="/news" 
              className="inline-flex items-center bg-red-600 hover:bg-red-700 text-white font-semibold py-3 sm:py-4 px-6 sm:px-8 rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl text-sm sm:text-base"
            >
              <span className="mr-2">{t('pages.home.allNews') || 'Barcha Yangiliklar'}</span>
              <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-12 sm:py-16 px-4">
          <div className="relative bg-gradient-to-r from-red-800 to-red-900 rounded-2xl sm:rounded-3xl p-8 sm:p-12 lg:p-16 text-center text-white overflow-hidden shadow-2xl">
            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute inset-0" style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
              }}></div>
            </div>
            
            <div className="relative z-10">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4 sm:mb-6">
                {t('pages.home.whyChooseUs') || 'Nima uchun bizni tanlaysiz?'}
              </h2>
              <p className="text-lg sm:text-xl mb-6 sm:mb-8 opacity-90 max-w-2xl mx-auto">
                {t('pages.home.whyChooseUsSubtitle') || "Yuqori sifat, ishonchli xizmat va professional yondashuv bilan xizmat ko'rsatamiz"}
              </p>
              <Link 
                to="/contact" 
                className="inline-flex items-center bg-red-600 hover:bg-red-700 text-white font-semibold py-3 sm:py-4 px-6 sm:px-8 rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl text-sm sm:text-base"
              >
                <span className="mr-2">{t('common.contactUs') || "Biz bilan bog'lanish"}</span>
                <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </Link>
            </div>
          </div>
        </section>
      </Container>

      <style jsx>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        
        .line-clamp-2 {
          overflow: hidden;
          display: -webkit-box;
          -webkit-box-orient: vertical;
          -webkit-line-clamp: 2;
        }
        
        .line-clamp-3 {
          overflow: hidden;
          display: -webkit-box;
          -webkit-box-orient: vertical;
          -webkit-line-clamp: 3;
        }
      `}</style>
    </div>
  );
};

export default Home;
